import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const propsData = [
  { icon: 'FiStar', title: 'GBP Optimization & Automation', description: 'Full-stack Google Business Profile management and reputation automation to dominate local search and capture high-intent traffic.' },
  { icon: 'FiGlobe', title: 'Free ADA/WCAG Websites', description: 'Compliant, high-performance sites built automatically at no extra cost for qualified users. Protect your business and convert more traffic.' },
];

export default function ValueProps({ ctaUrl }) {
  return (
    <section className="relative w-full min-h-full bg-[#202020] flex flex-col justify-center py-24 px-4 sm:px-6 md:px-12 overflow-hidden font-lexend">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />

      <div className="relative z-10 max-w-5xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-6 sm:mb-8">
          <span className="inline-block text-brand-teal font-bold text-xs uppercase tracking-[0.2em] mb-3">Included Value</span>
          <h2 className="text-2xl sm:text-3xl font-michroma text-white tracking-tight">The Complete Foundation</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-5 sm:mb-6">
          {propsData.map((prop, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }} className="group relative bg-[#181818] rounded-2xl p-5 sm:p-6 border border-white/5 transition-all duration-300 hover:-translate-y-1 hover:border-white/10">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl" style={{ boxShadow: 'inset 0 0 0 1px rgba(245,158,11,0.1), 0 0 30px rgba(245,158,11,0.05)' }} />
              <div className="relative z-10">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#f59e0b] to-[#eb5e55] flex items-center justify-center mb-4 shadow-lg shadow-orange-500/20">
                  <SafeIcon name={prop.icon} className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-michroma text-white mb-2 leading-snug">{prop.title}</h3>
                <p className="text-white/60 leading-relaxed text-xs sm:text-sm">{prop.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pricing */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="rounded-2xl border border-white/5 bg-[#181818] p-5 sm:p-6 mb-4">
          <div className="text-center mb-4">
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-teal">Pricing</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mb-4">
            <div className="flex flex-col items-center sm:items-start gap-0.5">
              <span className="font-michroma text-3xl text-white">$99</span>
              <span className="text-white/30 text-[10px] uppercase tracking-widest">per location / mo</span>
            </div>
            <div className="flex-1 flex flex-col items-center gap-1.5 px-4 sm:px-8">
              <div className="w-full h-1 rounded-full bg-white/5 relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 w-full rounded-full" style={{ background: 'linear-gradient(90deg,#289199,#f59e0b,#eb5e55)' }} />
              </div>
              <span className="text-white/30 text-[10px] uppercase tracking-widest">Scale as you grow</span>
            </div>
            <div className="flex flex-col items-center sm:items-end gap-0.5">
              <span className="font-michroma text-3xl text-white">$5,785</span>
              <span className="text-white/30 text-[10px] uppercase tracking-widest">enterprise / mo</span>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-brand-teal/10 border border-brand-teal/20 w-fit mx-auto">
            <SafeIcon name="FiMapPin" className="h-3.5 w-3.5 text-brand-teal" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-brand-teal">Multi-Location Discounts Available</span>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="rounded-2xl border border-brand-teal/20 bg-[#181818] p-6 sm:p-8 text-center" style={{ boxShadow: '0 0 50px rgba(40,145,153,0.08)' }}>
          <h3 className="font-michroma text-lg sm:text-xl text-white mb-2 leading-tight">
            Ready to Activate Your <span className="text-brand-teal">Authority Engine?</span>
          </h3>
          <p className="text-white/50 text-xs leading-relaxed max-w-lg mx-auto mb-1">
            Get your free report and custom plan — see exactly what's holding your local search ranking back.
          </p>
          <p className="text-brand-teal/70 text-[10px] font-bold uppercase tracking-widest mb-5">
            Starting at $99 per location — multi-location discounts available.
          </p>
          <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-lexend font-bold text-sm uppercase tracking-widest px-7 py-3.5 rounded-full bg-brand-teal text-white hover:bg-brand-teal/80 transition-all duration-200 shadow-xl shadow-brand-teal/25 hover:-translate-y-0.5">
            Get My Free Report & Plan
            <SafeIcon name="FiArrowRight" className="h-4 w-4" />
          </a>
          <p className="text-white/20 text-[11px] uppercase tracking-widest mt-3">No contracts. Cancel anytime.</p>
        </motion.div>

        {/* Neon Closing Section */}
        <motion.div 
          initial={{ opacity: 0 }} 
          whileInView={{ opacity: 1 }} 
          viewport={{ once: true }} 
          className="relative mt-16 sm:mt-24 w-full rounded-3xl overflow-hidden bg-[#050505] py-20 sm:py-28 px-4 flex flex-col items-center justify-center border border-white/5"
        >
          {/* Brick Wall Background */}
          <div 
            className="absolute inset-0 opacity-40 mix-blend-luminosity pointer-events-none"
            style={{
              backgroundImage: 'url("https://images.unsplash.com/photo-1511249969854-8c01df49961f?q=80&w=2070&auto=format&fit=crop")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505] pointer-events-none opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-[#050505] pointer-events-none opacity-80" />

          {/* Cowboy Image */}
          <motion.img 
            src="https://media-manager-c.questera.ai/greta-media/8210ad95efb747bb311b069cf26456dbc6f4698f846d83a8cc9125e272c4df314dbaa665e28d14a7414479ed5b72910d/images/aW1hZ2UvcG5n/90ee2408495496e48a48d20c8dfe859c.png"
            alt="Digital Cowboy"
            className="relative z-10 w-48 sm:w-64 object-contain mb-8 filter drop-shadow-[0_0_25px_rgba(245,158,11,0.25)]"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          />

          {/* Fading Neon Text */}
          <motion.div
            animate={{ opacity: [0.7, 1, 0.85, 1, 0.6, 1] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
            className="relative z-10 text-center mt-2"
          >
            <h2 
              style={{
                fontFamily: "'Pacifico', cursive",
                color: '#fff5e6',
                textShadow: '0 0 5px #fff, 0 0 10px #fff, 0 0 20px #f59e0b, 0 0 40px #ea580c, 0 0 80px #ea580c, 0 0 100px #ea580c'
              }}
              className="text-5xl sm:text-7xl md:text-8xl tracking-wider -rotate-2 px-4"
            >
              do something great
            </h2>
          </motion.div>
        </motion.div>

      </div>
    </section>
  );
}